import { useCallback, useEffect, useState } from 'react';

import { hrApi } from '@/api';

import { EmployeeSelect } from '@/components/selects';

import { PageHeader } from '@/components/layout/PageHeader';

import { Button } from '@/components/ui/Button';

import { DataTable, type Column } from '@/components/ui/DataTable';

import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';

import { Input } from '@/components/ui/Input';

import { Select } from '@/components/ui/Select';

import { FormGrid } from '@/components/ui/FormGrid';

import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';

import type { HrAppraisalCycle, HrEmployeeReview } from '@/types/hr';

import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';



export function HrAppraisalPage() {

  const { getLabel } = useEmployeeOptions({ hrScoped: true });

  const cyclesPagination = usePagination();

  const reviewsPagination = usePagination();

  const [cycles, setCycles] = useState<HrAppraisalCycle[]>([]);

  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);

  const [reviews, setReviews] = useState<HrEmployeeReview[]>([]);

  const [loading, setLoading] = useState(false);

  const [cycleName, setCycleName] = useState('');

  const [reviewEmployeeId, setReviewEmployeeId] = useState('');

  const [reviewerId, setReviewerId] = useState('');

  const [reviewScore, setReviewScore] = useState('');

  const [reviewComments, setReviewComments] = useState('');

  const [submittingReview, setSubmittingReview] = useState(false);



  const loadCycles = useCallback(async () => {

    setLoading(true);

    try {

      const data = await hrApi.listAppraisalCycles();

      setCycles(data);

      cyclesPagination.applyClientCount(data.length);

      if (data.length > 0 && selectedCycleId == null) {

        setSelectedCycleId(data[0].id);

      }

    } catch (err) {

      showApiError(err, 'Failed to load appraisal cycles');

    } finally {

      setLoading(false);

    }

  }, [selectedCycleId, cyclesPagination]);



  const loadReviews = useCallback(async (cycleId: number) => {

    try {

      const rows = await hrApi.listAppraisalReviews(cycleId);

      setReviews(rows);

      reviewsPagination.applyClientCount(rows.length);

    } catch (err) {

      showApiError(err, 'Failed to load reviews');

    }

  }, [reviewsPagination]);



  useEffect(() => {

    loadCycles();

  }, [loadCycles]);



  useEffect(() => {

    reviewsPagination.resetPage();

  }, [selectedCycleId, reviewsPagination]);



  useEffect(() => {

    if (selectedCycleId != null) {

      loadReviews(selectedCycleId);

    }

  }, [selectedCycleId, loadReviews]);



  const createCycle = async () => {

    if (!cycleName.trim()) return;

    try {

      await hrApi.createAppraisalCycle({

        cycleName: cycleName.trim(),

        startDate: new Date().toISOString().slice(0, 10),

        endDate: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),

        statusCode: 'ACTIVE',

      });

      notify.success('Appraisal cycle created');

      setCycleName('');

      loadCycles();

    } catch (err) {

      showApiError(err, 'Failed to create cycle');

    }

  };



  const submitReview = async () => {

    if (selectedCycleId == null || !reviewEmployeeId.trim()) {

      notify.error('Select a cycle and employee');

      return;

    }

    setSubmittingReview(true);

    try {

      await hrApi.submitAppraisalReview({

        appraisalCycleId: selectedCycleId,

        employeeId: Number(reviewEmployeeId),

        reviewerId: reviewerId.trim() ? Number(reviewerId) : undefined,

        score: reviewScore.trim() ? Number(reviewScore) : undefined,

        comments: reviewComments.trim() || undefined,

        statusCode: 'SUBMITTED',

      });

      notify.success('Review submitted');

      setReviewEmployeeId('');

      setReviewerId('');

      setReviewScore('');

      setReviewComments('');

      loadReviews(selectedCycleId);

    } catch (err) {

      showApiError(err, 'Failed to submit review');

    } finally {

      setSubmittingReview(false);

    }

  };



  const cycleColumns: Column<HrAppraisalCycle>[] = [

    { key: 'id', header: '#' },

    { key: 'cycleName', header: 'Cycle' },

    { key: 'startDate', header: 'Start' },

    { key: 'endDate', header: 'End' },

    { key: 'statusCode', header: 'Status' },

  ];



  const reviewColumns: Column<HrEmployeeReview>[] = [

    { key: 'employeeId', header: 'Employee', render: (row) => getLabel(row.employeeId, true) },

    { key: 'reviewerId', header: 'Reviewer', render: (row) => getLabel(row.reviewerId, true) },

    { key: 'score', header: 'Score' },

    { key: 'statusCode', header: 'Status' },

    { key: 'comments', header: 'Comments' },

  ];



  return (

    <div>

      <PageHeader

        title="Performance appraisal"

        description="Cycles, self/manager/HR reviews, and final ratings"

        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Appraisal' }]}

      />

      <div className="mb-4 flex flex-wrap gap-2">

        <Input

          placeholder="New cycle name"

          value={cycleName}

          onChange={(e) => setCycleName(e.target.value)}

        />

        <Button onClick={createCycle}>Create cycle</Button>

      </div>

      <DataTable

        columns={cycleColumns}

        data={paginateClientList(cycles, cyclesPagination.page, cyclesPagination.size)}

        loading={loading}

        keyField="id"

        {...dataTablePageProps(cyclesPagination)}

      />

      {selectedCycleId != null && (

        <div className="mt-8">

          <h3 className="mb-2 text-lg font-medium">Reviews (cycle #{selectedCycleId})</h3>

          <Select

            className="mb-4 max-w-xs"

            value={String(selectedCycleId)}

            onChange={(v) => setSelectedCycleId(Number(v))}

            options={cycles.map((c) => ({ value: String(c.id), label: c.cycleName }))}

          />



          <div className="mb-6 rounded-lg border border-slate-200 p-4 dark:border-slate-700">

            <h4 className="mb-2 text-sm font-medium">Submit review</h4>

            <FormGrid columns={4} className="items-end">

              <EmployeeSelect value={reviewEmployeeId} onChange={setReviewEmployeeId} hrScoped />

              <EmployeeSelect label="Reviewer" value={reviewerId} onChange={setReviewerId} hrScoped />

              <Input

                label="Score"

                type="number"

                value={reviewScore}

                onChange={(e) => setReviewScore(e.target.value)}

              />

              <Input

                label="Comments"

                value={reviewComments}

                onChange={(e) => setReviewComments(e.target.value)}

              />

              <Button loading={submittingReview} onClick={() => void submitReview()}>

                Submit review

              </Button>

            </FormGrid>

          </div>



          <DataTable

            columns={reviewColumns}

            data={paginateClientList(reviews, reviewsPagination.page, reviewsPagination.size)}

            keyField="id"

            {...dataTablePageProps(reviewsPagination)}

          />

        </div>

      )}

    </div>

  );

}


